import classNames from "classnames";
import React, {
  ForwardedRef,
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { FieldSchemaError } from "./useFieldSchema";
import {
  BaseFieldProps,
  CoreFieldProps,
  CoreFieldValue,
} from "../Fields/types/fieldTypes";
import { FieldSchemaFormBuilderV2 } from "./types";
import { FieldConfig } from "./FieldConfig";
import { get, has } from "lodash";
import FieldV2 from "./FieldV2";
import AppToasterV2 from "../ToastV2/ToastV2";

export const FormFieldSettingsContext = React.createContext<FormFieldSettings>({
  areAllFieldsDisabled: false,
  availableFields: [],
});

export interface FormFieldSettings {
  areAllFieldsDisabled?: boolean;
  availableFields?: string[];
}

export const FormFieldPropsContext = React.createContext<
  Partial<CoreFieldProps>[] | undefined
>(undefined);

export interface FieldFormV2Props<TModel> {
  /** Callback function called when the form is submitted. */
  onFormSubmit?: (value: TModel) => Promise<void>;
  // TODO Waiting for Dialog Implementation
  //shouldBlockNavigation?: boolean;
  /** Initial or State form value. */
  value?: TModel;
  /** Instance of the Field Schema Form Builder used to configure the form fields. */
  fieldSchemaFormBuilder?: FieldSchemaFormBuilderV2<TModel>; // optional, else you need to manage it on the fields like today
  /** Callback function called when a field value is changed. */
  onFieldChanged?: (
    value: TModel,
    newFieldValue: CoreFieldValue,
    fieldProps: BaseFieldProps<CoreFieldValue>
  ) => void;
  /** Object containing custom configuration for specific fields. */
  customFieldConfig?: FieldConfig;
  /** Callback function called when form validation fails. */
  onErrors?: (errors: { [key: string]: string[] }) => void;
  /** Boolean indicating whether the form is disabled or not. */
  isDisabled?: boolean;
  /** Array of field names that are available to the form. */
  availableFields?: string[];
  /** Indicates field positioning columns. Default 1. */
  columns?: number;
  children?: React.ReactNode;
  /** Override to pass in errors for the form. */
  errors?: { [key: string]: string[] };
  onReady?: () => void;
}

export interface FieldFormV2Module<TModel> {
  /** Resets the form to its initial state. */
  reset: () => void;
  /** Submits the form. */
  submit: () => Promise<boolean>;
  /** Returns an object containing form values. */
  getFormValue: () => TModel;
}

// Module augmentations allow you to extend the types of existing modules.
// https://www.digitalocean.com/community/tutorials/typescript-module-augmentation#enter-module-augmentation
declare module "react" {
  function forwardRef<T, P = {}>(
    render: (props: P, ref: React.Ref<T>) => React.ReactElement | null
  ): (props: P & React.RefAttributes<T>) => React.ReactElement | null;
}

const FieldFormV2Component = forwardRef(
  <TModel extends Object>(
    props: FieldFormV2Props<TModel>,
    ref: ForwardedRef<FieldFormV2Module<TModel>>
  ) => {
    useImperativeHandle(
      ref,
      () =>
        ({
          reset: () => {
            onValueChanged(
              resetValue.current,
              undefined,
              {} as BaseFieldProps<CoreFieldValue>
            );
          },
          getFormValue() {
            return formValue;
          },
          submit: async (): Promise<boolean> => {
            const form = formRef.current;
            if (form) {
              return await submitForm();
            }
            return Promise.resolve(false);
          },
        }) as FieldFormV2Module<TModel>
    );

    const formRef = useRef<HTMLFormElement>(null);
    const resetValue = useRef<TModel>({} as TModel);

    if (!props.value) {
      console.warn(
        "FormV2 Value is undefined. Please provide a value to the form to avoid rendering issues. If value comes from the API, do not load the form until value is populated."
      );
    }

    const applicableFormsFieldsReadyRef = useRef(0);
    const applicableFormsFieldsReadyLoadedRef = useRef(false);

    const [isFormDisabled, setIsFormDisabled] = useState<boolean | undefined>(
      false
    );
    const [availableFields, setAvailableFields] = useState<
      string[] | undefined
    >(props.availableFields);
    const [formValue, setFormValue] = useState<TModel>(
      props.value || ({} as TModel)
    );
    const [formFieldsData, setFormFieldsData] = useState<
      Partial<CoreFieldProps>[]
    >([]);

    useEffect(() => {
      setFormValue(props.value || ({} as TModel));
    }, [props.value]);

    useEffect(() => {
      resetValue.current = props.value || ({} as TModel);
    }, []);

    useEffect(() => {
      setIsFormDisabled(props.isDisabled);
    }, [props.isDisabled]);

    useEffect(() => {
      setAvailableFields(props.availableFields);
    }, [props.availableFields]);

    const [errors, setErrors] = useState<{
      [key: string]: string[];
    }>({});

    useEffect(() => {
      updateFormFieldData();
    }, [formValue, errors]);

    useEffect(() => {
      props.onErrors && props.onErrors(errors);
    }, [errors]);

    useEffect(() => {
      setErrors(props.errors || {});
    }, [props.errors]);

    const onValueChanged = (
      newValue: TModel,
      newFieldValue: CoreFieldValue,
      fieldProps: BaseFieldProps<CoreFieldValue>
    ) => {
      // TODO: Consider using hash to compare objects

      setFormValue({ ...newValue });
      props.onFieldChanged &&
        props.onFieldChanged(newValue, newFieldValue, fieldProps);
    };

    const updateFormFieldData = () => {
      if (!props.fieldSchemaFormBuilder) {
        return;
      }

      const fields = props.fieldSchemaFormBuilder(
        onValueChanged,
        formValue,
        errors,
        props.onFormSubmit
      );
      setFormFieldsData([...fields]);
    };

    const submitForm = (): Promise<boolean> => {
      if (!props.onFormSubmit) {
        return Promise.resolve(true);
      }

      return props
        .onFormSubmit(formValue)
        .then(() => {
          // Keep value in sync with form since form reset value after submit.
          setFormValue(formValue);
          props.onErrors && props.onErrors({});
          return true;
        })
        .catch((error) => {
          console.log(error);
          if (error.response?.data?.errors) {
            console.error("Runtime Crash Error", error.response.data.errors);

            AppToasterV2.showFailedActionToaster(
              "An error occurred executing the current action."
            );
          } else {
            setErrors({
              ...((error as FieldSchemaError)?.data ||
                error.response.data.additionalInfo),
            });
          }
          return Promise.resolve(false);
        });
    };

    const applicableFormsFields = formFieldsData.filter((x) =>
      x.type !== "Abstract" && !x.hidden && (availableFields || []).length > 0
        ? availableFields?.includes(x.fieldKey || "")
        : true
    );

    const handleReady = () => {
      applicableFormsFieldsReadyRef.current++;
      if (
        !applicableFormsFieldsReadyLoadedRef.current &&
        applicableFormsFieldsReadyRef.current >= applicableFormsFields.length
      ) {
        applicableFormsFieldsReadyLoadedRef.current = true;
        props.onReady && props.onReady();
      }
    };

    return (
      <>
        {/* TODO Waiting for Dialog Implementation */}
        {/* {props.shouldBlockNavigation === true && (
        <SaveChangeDialog
          isDirty={
            JSON.stringify(props.value) !==
            JSON.stringify(props.formValueState.formValue)
          }
        />
      )} */}
        <form
          ref={formRef}
          className={classNames(
            `field-form${
              props.columns
                ? " field-form-grid field-form-grid-" + props.columns
                : ""
            }`,
            { "field-form-disabled": isFormDisabled },
            {
              "field-form-available-fields": (availableFields || []).length > 0,
            }
          )}
          onSubmit={(e) => {
            e.preventDefault();
            submitForm();
          }}
        >
          <fieldset>
            <FormFieldSettingsContext.Provider
              value={{
                areAllFieldsDisabled: isFormDisabled,
                availableFields: availableFields,
              }}
            >
              <FormFieldPropsContext.Provider value={formFieldsData}>
                {props.children &&
                  React.Children.map(props.children, (child) =>
                    React.isValidElement<{
                      errors?: { [key: string]: string[] }; // Adjust type as needed
                    }>(child)
                      ? React.cloneElement(child, { errors: errors })
                      : child
                  )}
                {!props.children && (
                  <>
                    {applicableFormsFields.map((field, idx) => {
                      const renderKey = has(field, "renderKey")
                        ? `${get(field, "renderKey")}idx`
                        : idx;

                      if (field.fieldName === undefined) {
                        throw new Error(
                          "Field name is required, but is undefined on the provided field schema."
                        );
                      }

                      if (field.fieldKey === undefined) {
                        throw new Error(
                          "Field Key is required, but is undefined on the provided field schema."
                        );
                      }

                      return (
                        <div
                          className={classNames(
                            "field-form-field",
                            `field-form-field-${field.width || 100}`
                          )}
                          key={renderKey}
                        >
                          <FieldV2
                            fieldProps={field.fieldKey}
                            customFieldConfig={props.customFieldConfig}
                            onReady={handleReady}
                          />
                        </div>
                      );
                    })}
                  </>
                )}
              </FormFieldPropsContext.Provider>
            </FormFieldSettingsContext.Provider>
          </fieldset>
        </form>
      </>
    );
  }
);

export const FieldFormV2 = FieldFormV2Component;
export default FieldFormV2;
